import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useDispatch } from 'react-redux';
import { updateLeadNote } from '../project/actions';

import EmptyState from 'shared/empty_state';
import FilterButton from 'shared/filter_button';
import FilterCheckboxGroup from 'components/search_sidebar/filter_checkbox_group';

import Pagination from 'shared/pagination';
import { Table } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faArrowDownArrowUp,
  faCheckCircle
} from '@fortawesome/pro-solid-svg-icons';
import {
  COLUMN_KEY_TO_PROPERTY_MAP,
  LEAD_COLUMNS,
  DEFAULT_PAGE_SIZE
} from './leads_card_util';

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

type Leads = {
  id: number,
  displayName: string,
  contactId: number,
  contactCompanyName: string,
  leadStatusId: number,
  leadStatusLabel: string,
  pageViews: number,
  lastActionLabel: string,
  notes: string
}

type FilterOption = {
  id: string,
  label: string,
  value: string
}

export type Props = {
  leads: Leads[],
  leadFilters: {
    companies: FilterOption[],
    projectLeadStatuses: FilterOption[],
    actions: FilterOption[]
  },
  filters?: {
    company?: string[],
    lastActions?: string[],
    leadStatus?: string[],
    page?: number,
    sort?: {
      column: string,
      sortDesc: boolean
    }
  },
  isHidden?: boolean,
  onFilterUpdate?: (filters: any) => void,
  preview?: boolean,
  projectId: number,
  visibleColumns: string[]
}

export default function LeadsCard({
  leads = [],
  leadFilters,
  filters = {},
  isHidden,
  onFilterUpdate,
  preview = false,
  projectId,
  visibleColumns
}: Props) {
  const [sort, setSort] = useState({
    column: filters.sort?.column || 'companyName',
    sortDesc: filters.sort?.sortDesc !== undefined ? filters.sort.sortDesc : true
  });
  const [page, setPage] = useState(filters.page ? Number(filters.page) : 1);
  const pageSize = DEFAULT_PAGE_SIZE;

  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(
    filters.company || []
  );
  const [selectedActions, setSelectedActions] = useState<string[]>(
    filters.lastActions || []
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    filters.leadStatus || []
  );
  const [originalNotes, setOriginalNotes] = useState<Record<number, string>>({});
  const [leadNotes, setLeadNotes] = useState<Record<number, string>>({});
  const [saveStatus, setSaveStatus] = useState<{
    leadId: number | null, status: 'success' | 'error' | null
  }>({ leadId: null, status: null });

  const companyOptions = useMemo(() => leadFilters?.companies || [], [leadFilters]);
  const actionOptions = useMemo(() => leadFilters?.actions || [], [leadFilters]);
  const statusOptions = useMemo(() => leadFilters?.projectLeadStatuses || [], [leadFilters]);

  // The "All options" options
  const companyOptionsWithAll = [
    { id: 'all-companies', label: 'All companies', value: 'all-companies' },
    ...companyOptions
  ];

  const actionOptionsWithAll = [
    { id: 'all-actions', label: 'All actions', value: 'all-actions' },
    ...actionOptions
  ];

  const statusOptionsWithAll = [
    { id: 'all-statuses', label: 'All statuses', value: 'all-statuses' },
    ...statusOptions
  ];

  const allCompaniesSelected = selectedCompanies.includes('all-companies');
  const allActionsSelected = selectedActions.includes('all-actions');
  const allStatusesSelected = selectedStatuses.includes('all-statuses');

  const containerRef = useRef(null);

  const prevFilters = usePrevious<typeof filters>(filters);

  const dispatch = preview ? null : useDispatch();

  useEffect(() => {
    if (prevFilters && !isEqual(prevFilters, filters)) {
      if (filters.company) setSelectedCompanies(filters.company);
      if (filters.lastActions) setSelectedActions(filters.lastActions);
      if (filters.leadStatus) setSelectedStatuses(filters.leadStatus);
      if (filters.page) setPage(filters.page);
      if (filters.sort) setSort(filters.sort);
    }
  }, [filters, prevFilters]);

  useEffect(() => {
    const newOriginalNotes = leads.reduce((acc, lead) => ({ ...acc, [lead.id]: lead.notes || '' }), {});
    const newLeadNotes = leads.reduce((acc, lead) => ({ ...acc, [lead.id]: lead.notes || '' }), {});

    setOriginalNotes(newOriginalNotes);
    setLeadNotes(newLeadNotes);
  }, [leads]);

  useEffect(() => {
    if (prevFilters && leads.length > 0) {
      const filtersChanged =
        !isEqual(prevFilters?.company ?? [], filters?.company ?? []) ||
        !isEqual(prevFilters?.lastActions ?? [], filters?.lastActions ?? []) ||
        !isEqual(prevFilters?.leadStatus ?? [], filters?.leadStatus ?? []);

      if (filtersChanged) {
        setPage(1);

        if (onFilterUpdate) {
          onFilterUpdate({
            ...filters,
            page: 1
          });
        }
      }
    }
  }, [leads, filters, prevFilters, onFilterUpdate]);

  const updateFilters = useCallback((filterUpdates) => {
    if (!preview && onFilterUpdate) {
      onFilterUpdate(filterUpdates);
    }
  }, [preview, onFilterUpdate]);

  const handleFilterChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    filterKey: 'company' | 'lastActions' | 'leadStatus'
  ) => {
    const { checked, value } = e.target;

    const filterConfig = {
      company: {
        options: companyOptions,
        selected: selectedCompanies,
        setter: setSelectedCompanies,
        allValue: 'all-companies',
      },
      lastActions: {
        options: actionOptions,
        selected: selectedActions,
        setter: setSelectedActions,
        allValue: 'all-actions',
      },
      leadStatus: {
        options: statusOptions,
        selected: selectedStatuses,
        setter: setSelectedStatuses,
        allValue: 'all-statuses',
      },
    };

    const { options, selected, setter, allValue } = filterConfig[filterKey];

    let newValues: string[];
    if (value === allValue) {
      if (checked) {
        newValues = [allValue, ...options.map(option => option.value)];
      } else {
        newValues = [];
      }
    } else {
      if (checked) {
        newValues = [...selected.filter(v => v !== allValue), value];
      } else {
        newValues = selected.filter(v => v !== value && v !== allValue);
      }
    }

    setter(newValues);
    setPage(1);

    const updatedFilters = {
      company: selectedCompanies,
      lastActions: selectedActions,
      leadStatus: selectedStatuses,
      [filterKey]: newValues,
      page: 1,
      sort,
    };

    updateFilters(updatedFilters);
  }, [
    companyOptions, selectedCompanies,
    actionOptions, selectedActions,
    statusOptions, selectedStatuses,
    sort, updateFilters,
  ]);

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      if (selectedCompanies.length > 0 && !allCompaniesSelected) {
        const hasCompanyName = lead.contactCompanyName && lead.contactCompanyName.trim() !== '';
        if (!hasCompanyName || !selectedCompanies.includes(lead.contactCompanyName)) {
          return false;
        }
      }

      if (selectedActions.length > 0 && !allActionsSelected) {
        if (!selectedActions.includes(lead.lastActionLabel)) {
          return false;
        }
      }

      if (selectedStatuses.length > 0 && !allStatusesSelected) {
        if (!selectedStatuses.includes(lead.leadStatusLabel)) {
          return false;
        }
      }

      return true;
    });
  }, [leads,
    selectedCompanies,
    selectedActions,
    selectedStatuses,
    allCompaniesSelected,
    allActionsSelected,
    allStatusesSelected]
  );

  const totalPages = useMemo(() => Math.ceil(filteredLeads.length / pageSize), [filteredLeads.length, pageSize]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const processedLeads = useMemo(() => {
    const sortedData = [...filteredLeads].sort((a, b) => {
      const property = COLUMN_KEY_TO_PROPERTY_MAP[sort.column];
      const valueA = a[property];
      const valueB = b[property];

      if (typeof valueA === 'number') {
        return sort.sortDesc ? valueB - valueA : valueA - valueB;
      }

      const strA = valueA ? String(valueA).toLowerCase() : '';
      const strB = valueB ? String(valueB).toLowerCase() : '';

      return sort.sortDesc ? strB.localeCompare(strA) : strA.localeCompare(strB);
    });

    const startIndex = (page - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredLeads, sort.column, sort.sortDesc, page, pageSize]);

  const handleSortChange = useCallback((columnKey) => {
    const newSort = (prevSort) => {
      if (prevSort.column === columnKey) {
        return { ...prevSort, sortDesc: !prevSort.sortDesc };
      } else {
        return { column: columnKey, sortDesc: true };
      }
    };

    setSort((prevSort) => {
      const updatedSort = newSort(prevSort);

      updateFilters({
        company: selectedCompanies,
        lastActions: selectedActions,
        leadStatus: selectedStatuses,
        page: 1,
        sort: updatedSort
      });

      return updatedSort;
    });

    setPage(1);
  }, [selectedCompanies, selectedActions, selectedStatuses, updateFilters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);

    updateFilters({
      company: selectedCompanies,
      lastActions: selectedActions,
      leadStatus: selectedStatuses,
      page: newPage,
      sort: sort
    });
  }, [selectedCompanies, selectedActions, selectedStatuses, sort, updateFilters]);

  const columnHeaders = useMemo(() => {
    return Object.keys(LEAD_COLUMNS).map((columnName) => {
      if (preview && !visibleColumns.includes(LEAD_COLUMNS[columnName].key)) {
        return null;
      }

      const column = LEAD_COLUMNS[columnName];
      const isSortColumn = column.key === sort.column;

      return (
        <th
          className={`text-nowrap ${column.sortable ? 'cursor-pointer' : ''}`}
          key={columnName}
          onClick={column.sortable ? () => handleSortChange(column.key) : undefined}
        >
          <div className="d-flex align-items-center">
            {column.header}
            {column.sortable && (
              <span className="ms-1">
                <FontAwesomeIcon
                  icon={isSortColumn
                    ? (sort.sortDesc ? column.icon.desc : column.icon.asc)
                    : faArrowDownArrowUp}
                />
              </span>
            )}
          </div>
        </th>
      );
    });
  }, [sort.column, sort.sortDesc, handleSortChange]);

  const renderLeadFilters = () => {
    return (
      <div className="lead-filters d-flex flex-wrap mb-3">
        {((preview && visibleColumns.includes('companyName')) || !preview) &&
          <div className="flex-fill px-2">
            <h6 className="pb-2"><b>Company</b></h6>
            <div className="leads-filter-dropdown position-relative">
              <FilterButton
                additionalClasses="w-100"
                container={containerRef.current}
                filtersApplied={selectedCompanies.length > 0}
                label="All companies"
                name="companiesFilter"
              >
                <FilterCheckboxGroup
                  options={companyOptionsWithAll}
                  selected={selectedCompanies}
                  onChange={e => handleFilterChange(e, 'company')}
                />
              </FilterButton>
            </div>
          </div>
        }
        {((preview && visibleColumns.includes('lastAction')) || !preview) &&
          <div className="flex-fill px-2">
            <h6 className="pb-2"><b>Last Action</b></h6>
            <div className="leads-filter-dropdown position-relative">
              <FilterButton
                additionalClasses="w-100"
                container={containerRef.current}
                filtersApplied={selectedActions.length > 0}
                label="All actions"
                name="actionsFilter"
              >
                <FilterCheckboxGroup
                  options={actionOptionsWithAll}
                  selected={selectedActions}
                  onChange={e => handleFilterChange(e, 'lastActions')}
                />
              </FilterButton>
            </div>
          </div>
        }
        {((preview && visibleColumns.includes('leadStatus')) || !preview) &&
          <div className="flex-fill px-2">
            <h6 className="pb-2"><b>Lead Status</b></h6>
            <div className="leads-filter-dropdown position-relative">
              <FilterButton
                additionalClasses="w-100"
                container={containerRef.current}
                filtersApplied={selectedStatuses.length > 0}
                label="All Statuses"
                name="statusesFilter"
              >
                <FilterCheckboxGroup
                  options={statusOptionsWithAll}
                  selected={selectedStatuses}
                  onChange={e => handleFilterChange(e, 'leadStatus')}
                />
              </FilterButton>
            </div>
          </div>
        }
      </div>
    );
  };

  const updateNote = (leadId: number, body: string) => {
    setLeadNotes(prev => ({ ...prev, [leadId]: body }));
  };

  const SAVE_STATUS_TIMEOUT_MS = 1500;

  const saveContent = async (leadId: number) => {
    if (preview || !dispatch) return;

    const contentToSave = leadNotes[leadId] || '';
    const originalContent = originalNotes[leadId] || '';

    if (contentToSave !== originalContent) {
      try {
        await dispatch(updateLeadNote(projectId, { id: leadId, noteToClient: { body: contentToSave } }));
        setOriginalNotes(prev => ({ ...prev, [leadId]: contentToSave }));
        setSaveStatus({ leadId, status: 'success' });
        setTimeout(() => setSaveStatus({ leadId: null, status: null }), SAVE_STATUS_TIMEOUT_MS);
      } catch (error) {
        setSaveStatus({ leadId, status: 'error' });
        setTimeout(() => setSaveStatus({ leadId: null, status: null }), SAVE_STATUS_TIMEOUT_MS);
      }
    }
  };

  const renderNotes = (lead: Leads) => {
    return (
      <div>
        <div style={{ position: 'relative' }}>
          <textarea
            className="form-control w-100"
            data-lead-id={lead.id}
            name={`leadNoteToClient_${lead.id}`}
            placeholder="Add Lead comments to your client here (optional)..."
            readOnly={preview}
            rows={2}
            style={{
              minHeight: '2em',
              minWidth: '100%',
              backgroundColor: isHidden
                ? 'var(--ds-storm-grey-100, #eeeef2)'
                : preview
                  ? '#fff'
                  : undefined
            }}
            value={leadNotes[lead.id]}
            onBlur={() => saveContent(lead.id)}
            onChange={e => updateNote(lead.id, e.target.value)}
            onMouseDown={preview ? e => e.preventDefault() : undefined}
          />
          {saveStatus.leadId === lead.id && saveStatus.status === 'success' && (
            <div className="text-success mt-1 small">
              <FontAwesomeIcon
                color="green"
                icon={faCheckCircle}
                title="Saved!"
              /> Saved!
            </div>
          )}
          {saveStatus.leadId === lead.id && saveStatus.status === 'error' && (
            <div className="text-danger mt-1 small">
              <FontAwesomeIcon color="red" icon={faExclamationCircle} /> Failed to save.
            </div>
          )}
        </div>
      </div>
    );
  };

  const hiddenColumnClass = (columnKey: string) => {
    if (!visibleColumns.includes(columnKey)) {
      return 'ds-column-preview-hidden';
    }
    return '';
  };

  const renderLeadTable = () => {
    return (
      <div className="ds-leads-table-container">
        <Table className="ds-table">
          <thead className="ds-leads-table-header">
            <tr>
              {columnHeaders}
            </tr>
          </thead>
          <tbody>
            {processedLeads.map(lead => (
              <tr key={lead.id}>
                {((preview && visibleColumns.includes('companyName')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('companyName')}`}>
                    {lead.contactCompanyName}
                  </td>
                }
                {((preview && visibleColumns.includes('name')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('name')}`}>
                    {lead.displayName}
                  </td>
                }
                {((preview && visibleColumns.includes('leadStatus')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('leadStatus')}`}>
                    {lead.leadStatusLabel}
                  </td>
                }
                {((preview && visibleColumns.includes('pageViews')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('pageViews')}`}>
                    {lead.pageViews}
                  </td>
                }
                {((preview && visibleColumns.includes('lastAction')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('lastAction')}`}>
                    {lead.lastActionLabel}
                  </td>
                }
                {((preview && visibleColumns.includes('notes')) || !preview) &&
                  <td className={`align-middle ${hiddenColumnClass('notes')}`} style={{ whiteSpace: 'pre-wrap' }}>
                    {renderNotes(lead)}
                  </td>
                }
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderPagination = () => {
    return totalPages > 1 ? (
      <div className="text-center">
        <Pagination
          currentPage={page}
          showFirstLast={true}
          showJumpTo={true}
          showLabels={true}
          totalPages={totalPages}
          onChange={handlePageChange}
        />
      </div>
    ) : null;
  };

  const renderEmptyState = () => {
    return (
      <EmptyState
        bodyText="Waiting For Data..."
        header="No Leads to display"
        icon={faExclamationCircle}
        style={{ minHeight: '250px' }}
      />
    );
  };

  const renderLeadsCard = () => {
    return (
      <>
        {renderLeadFilters()}
        {renderLeadTable()}
        {renderPagination()}
      </>
    );
  };

  return (
    <div className="ds-client-report-card d-flex flex-column gap-4 p-4" ref={containerRef}>
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="m-0 fw-normal">Leads</h6>
      </div>
      {
        leads.length === 0 ?
          renderEmptyState() :
          renderLeadsCard()
      }
    </div>
  );
}
